import React from 'react'
import {useTranslation} from 'react-i18next'

import {
  shouldShowGross,
  showVatNote,
  vatPriceNote,
  vatDisplayAmount,
  vatSaveAmount,
  VatEstimateLike,
} from '../../../utils/vatPriceDisplay'
import {toCents, fromCents} from '../../../utils/vatMoney'

interface Props {
  /** The plan's net (VAT-exclusive) list price. */
  price: string | number
  /**
   * The net price the member actually pays, when the plan has a discount (`discount_flag === 1`).
   * Omit for a plan with no discount — `price` is then both the struck price and the headline.
   */
  discountPrice?: string | number
  /** The IP-estimated VAT lookup. `null`/`undefined` covers both "still loading" and "failed". */
  estimate?: VatEstimateLike | null
}

/**
 * The shop price cell shared by Baseplan, Affiliateplan, AddonsPage and AddonsAddonsPage.
 *
 * - Personal EU visitor (estimate.is_eu && rate > 0): headline is the GROSS price, with the net
 *   price shown secondarily and an "incl. VAT" note.
 * - Everything else — non-EU, a zero rate, or no estimate yet (loading/failed, which never
 *   throws — see `vatApi.getVatEstimate`) — headline is the plain NET price, with a
 *   "VAT calculated at checkout" note. This component never blocks render on the estimate.
 * - A discount (`discountPrice` given) grosses up BOTH the struck-through original price and the
 *   live discounted price; "you save" is computed on the same (gross or net) figures.
 */
export function VatPriceBlock({price, discountPrice, estimate}: Props) {
  const {t} = useTranslation()

  const hasDiscount = discountPrice !== undefined && discountPrice !== null
  const actualPrice = hasDiscount ? (discountPrice as string | number) : price

  const gross = shouldShowGross(estimate)
  const headline = vatDisplayAmount(actualPrice, estimate)
  const secondaryNet = gross ? fromCents(toCents(actualPrice)) : undefined
  const note = t(vatPriceNote(estimate))

  const secondaryAndNote = (
    <>
      {secondaryNet !== undefined && (
        <div className='text-center vat-price-secondary'>
          <span className='text-muted fs-7'>$ {secondaryNet.toFixed(2)} net</span>
        </div>
      )}
      {showVatNote(estimate) && (
        <div className='text-center vat-price-note'>
          <span className='text-muted fs-8'>{note}</span>
        </div>
      )}
    </>
  )

  if (hasDiscount) {
    const original = vatDisplayAmount(price, estimate)
    const save = vatSaveAmount(price, discountPrice as string | number, estimate)

    return (
      <div className='plan-item-price'>
        <div className='text-center'>
          <span className='mb-2 text-primary'>$</span>
          <span className='fs-2x fw-bolder text-primary' style={{textDecoration: 'line-through'}}>
            {original.toFixed(2)}
          </span>
        </div>
        <div className='text-center mb-1'>
          <span className='text-primary fw-bold fs-3'>
            {t('WEBSHOP_YOU_SAVE', {amount: save.toFixed(2)})}
          </span>
        </div>
        <div className='text-center'>
          <span className='mb-2 text-primary'>$</span>
          <span className='fs-3x fw-bolder text-primary'>{headline.toFixed(2)}</span>
        </div>
        {secondaryAndNote}
      </div>
    )
  }

  return (
    <>
      {/* `.plan-item-discount` is display:flex (centers the $ + amount on one row); keep the
          secondary/net + VAT note OUTSIDE it so they stack BELOW the price instead of beside it. */}
      <div className='text-center plan-item-discount'>
        <span className='mb-2 text-primary'>$</span>
        <span className='fs-3x fw-bolder text-primary'>{headline.toFixed(2)}</span>
      </div>
      {secondaryAndNote}
    </>
  )
}
